import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { reviewAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { capitalize } from '@/lib/utils';
import { ArrowLeft } from 'lucide-react';

export default function EmailDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedResponse, setEditedResponse] = useState('');

  const { data: email, isLoading, refetch } = useQuery({
    queryKey: ['email', id],
    queryFn: async () => {
      const response = await reviewAPI.getById(id!);
      return response.data;
    },
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (response: string) => reviewAPI.updateResponse(id!, response),
    onSuccess: () => {
      toast.success('Response updated');
      setIsEditing(false);
      refetch();
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => reviewAPI.approve(id!, 'admin'),
    onSuccess: () => {
      toast.success('Email approved');
      navigate('/review');
    },
  });

  const approveAndSendMutation = useMutation({
    mutationFn: () => reviewAPI.approveAndSend(id!, 'admin'),
    onSuccess: () => {
      toast.success('Email approved and sent successfully');
      navigate('/review');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => reviewAPI.reject(id!, 'admin'),
    onSuccess: () => {
      toast.success('Email rejected');
      navigate('/review');
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: () => reviewAPI.reprocess(id!),
    onSuccess: () => {
      toast.success('Email moved back to review queue');
      refetch();
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => reviewAPI.send(id!),
    onSuccess: () => {
      toast.success('Email sent successfully');
      refetch();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!email) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">Email not found</p>
        </CardContent>
      </Card>
    );
  }

  const handleSaveEdit = () => {
    updateMutation.mutate(editedResponse);
  };

  const handleStartEdit = () => {
    setEditedResponse(email.edited_response || email.response_text || '');
    setIsEditing(true);
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/review')}
        size="sm"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Review Queue
      </Button>

      {/* Email Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-xl sm:text-2xl mb-2 break-words">{email.subject}</CardTitle>
                <CardDescription className="text-sm sm:text-base break-words">
                  From: {email.from_address.split('<')[0].trim() || email.from_address}
                </CardDescription>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {email.response_status === 'pending_review' && (
                  <>
                    <Button
                      onClick={() => approveMutation.mutate()}
                      disabled={approveMutation.isPending}
                      variant="outline"
                      size="sm"
                      className="border-green-200 text-green-700 hover:bg-green-50"
                    >
                      Approve
                    </Button>
                    <Button
                      onClick={() => approveAndSendMutation.mutate()}
                      disabled={approveAndSendMutation.isPending}
                      variant="outline"
                      size="sm"
                      className="border-green-200 text-green-700 hover:bg-green-50"
                    >
                      Approve & Send
                    </Button>
                    <Button
                      onClick={() => rejectMutation.mutate()}
                      disabled={rejectMutation.isPending}
                      variant="outline"
                      size="sm"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                    >
                      Reject
                    </Button>
                  </>
                )}
                {email.response_status === 'approved' && (
                  <Button
                    onClick={() => sendMutation.mutate()}
                    disabled={sendMutation.isPending}
                    variant="outline"
                    size="sm"
                  >
                    Send Email
                  </Button>
                )}
                {email.response_status === 'rejected' && (
                  <Button
                    onClick={() => reprocessMutation.mutate()}
                    disabled={reprocessMutation.isPending}
                    variant="outline"
                    size="sm"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  >
                    Reprocess
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary">
                {capitalize(email.category || 'N/A')}
              </Badge>
              <Badge variant="outline">
                {capitalize(email.response_status || 'pending')}
              </Badge>
              {email.extracted_entities?.order_id && (
                <Badge variant="outline" className="break-all">
                  Order: {email.extracted_entities.order_id}
                </Badge>
              )}
              <span className="text-sm text-muted-foreground">
                {new Date(email.received_at || email.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Customer Message */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Customer Message</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/30 rounded-lg p-4 sm:p-6 border overflow-x-auto">
            <div 
              className="text-sm whitespace-pre-wrap leading-relaxed break-words"
              dangerouslySetInnerHTML={{ __html: email.body }}
            />
          </div>
        </CardContent>
      </Card>

      {/* AI Generated Response */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Generated Response</CardTitle>
            {!isEditing && email.response_status === 'pending_review' && (
              <Button
                onClick={handleStartEdit}
                variant="outline"
                size="sm"
              >
                Edit Response
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <textarea
                value={editedResponse}
                onChange={(e) => setEditedResponse(e.target.value)}
                rows={16}
                className="w-full px-4 py-3 text-sm border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-input bg-background resize-none"
                placeholder="Edit the response..."
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveEdit}
                  disabled={updateMutation.isPending}
                  size="sm"
                  variant="outline"
                  className="border-green-200 text-green-700 hover:bg-green-50"
                >
                  Save Changes
                </Button>
                <Button
                  onClick={() => setIsEditing(false)}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-muted/30 rounded-lg p-4 sm:p-6 border overflow-x-auto">
              <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">
                {email.edited_response || email.response_text || 'No response generated'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
